<template>
  <vl-multiselect
    v-bind="$attrs"
    :id="id"
    ref="multiselect"
    v-model="data.value"
    :mode="`single`"
    :options="data.options"
    :label="labelProp"
    :valueProp="valueProp"
    :trackBy="trackBy"
    :object="true"
    :allowEmpty="true"
    :searchable="searchable"
    :closeOnSelect="true"
    @update:model-value="handleUpdateModelValue"
    @select="handleSelect"
    @deselect="handleRemove"
    @search-change="handleSearchChange"
  >
    <template v-slot:noresults>
      <div class="noResults">
        {{ noResultText }}
      </div>
    </template>
  </vl-multiselect>
</template>

<script lang="ts" setup>
import { PropType, computed, useAttrs, watch } from 'vue';
import { reactive } from 'vue';

export type PbsSelectSortFunction = (a: any, b: any) => number;

defineOptions({
  inheritAttrs: false
});

const emit = defineEmits(['update:modelValue', 'search-change', 'select', 'remove']);

const props = defineProps({
  id: {
    type: String,
    required: true
  },
  options: {
    type: Array,
    required: true
  },
  modelValue: {
    type: [String, Number, Object],
    default: '',
    required: false
  },
  valueProperty: {
    type: String,
    required: false,
    default: ''
  },
  labelProperty: {
    type: String,
    required: false,
    default: ''
  },
  customLabel: {
    type: Function,
    required: true,
    default: (option: any) => option.name
  },
  sortFunction: {
    type: Function as PropType<PbsSelectSortFunction>,
    required: false,
    default: null
  },
  noResultText: {
    type: String,
    required: false,
    default: 'Geen resultaten gevonden'
  },
  searchable: {
    type: Boolean,
    required: false,
    default: true
  }
});

/*
  Reactive data object to hold the internal model value and options.
*/
const data = reactive({
  value: null as any,
  options: []
});

/*
 the labelProp is used to determine the label that will be rendered for each item in the dropdownlist.
 The property will always be added (if not exists) to each value of the reactive options.
 and therefore also to the model value.
*/
const labelProp = computed(() => props.labelProperty || 'displayValue');

/*
  the valueProp is either the valueProperty provided via props or 'id' by default
  It is used to identify each option value uniquely.
*/
const valueProp = computed(() => props.valueProperty || 'id');

/*
  trackBy is an array of properties that will be used to track the option values.
  It is used to determine if the option value has changed.
  It is also used for searching and filtering. All property values in this array will be searchable.
*/
const trackBy = computed(() => [labelProp.value, valueProp.value]);

/*
  Creates new option objects with the labelProp added.
  The options array from the props will not be modified directly.
*/
const createOptions = (options: any[]): any[] => {
  if (!Array.isArray(options)) {
    return [];
  }
  return options.map((opt) => ({
    ...(opt as Object),
    [labelProp.value]: getLabelValue(opt)
  }));
};

/*
  Sorts the options array using the provided sortFunction or the defaultSort function.
*/
const sortOptions = (options: any[]): any[] => {
  if (options) {
    const sortFunction = props.sortFunction ?? defaultSort;
    return options.sort((a, b) => sortFunction(a, b));
  }
  return [];
};

/*
  Default sort function that sorts options alphabetically based on their label.
*/
const defaultSort = (optionA: any, optionB: any): number => {
  const labelA = getLabelValue(optionA);
  const labelB = getLabelValue(optionB);
  return labelA.localeCompare(labelB);
};

/*
  Filters the options array from the props based on the search term.
*/
const filterOptions = (search: string): any[] => {
  const filteredOptions = props.options.filter(
    (option) => !search || getLabelValue(option).toLowerCase().includes(search.toLowerCase())
  );
  return filteredOptions;
};

/*
  Gets the label value for an option using the labelProp or the customLabel function.
*/
const getLabelValue = (option: any): string => {
  let label = option[labelProp.value];
  if (!label && props.customLabel) {
    label = props.customLabel(option);
  }
  if (!label) {
    label = option.name;
  }
  return label || '';
};

const handleSearchChange = (searchTerm: string) => {
  emit('search-change', searchTerm);
};

/*
  Handles the update of the internal model value and emits the update:modelValue event.
  The emitted value is either the full option object or the value selected by the valueProperty.
*/
const handleUpdateModelValue = (value: any) => {
  data.value = value;
  const modelValue = props.valueProperty ? value?.[props.valueProperty] : value;
  emit('update:modelValue', modelValue);
};

/*
  Emits the select event when an option is selected.
*/
const handleSelect = (value: any) => {
  emit('select', value);
};

/*
  Emits the remove event when an option is deselected.
*/
const handleRemove = (value: any) => {
  emit('remove', value);
};

/*
  Sets the internal model value based on the input value.
  If valueProperty is provided, the value is compared with the option value using the valueProperty
  else the value is compared by the option id property
*/
const setInternalValue = (value: any) => {
  data.value = value
    ? data.options?.find((option) => {
        if (props.valueProperty) {
          return option[props.valueProperty] === value;
        } else {
          return option['id'] === value['id'];
        }
      })
    : null;
};

const createAndSortOptions = (options: any[], search: string = null) => {
  const opts = search ? filterOptions(search) : options;
  return sortOptions(createOptions(opts));
};

/*
  Updates the reactive value every time the modelValue prop changes.
*/
watch(
  () => props.modelValue,
  (newValue) => setInternalValue(newValue)
);

/*
  Updates the reactive options every time the options prop changes.
  It also updates the internal model value based on the new options prop and the modelValue prop.
  The update is immediately executed on component mount (so not needed in onMounted event).
*/
watch(
  () => props.options,
  (newValue) => {
    data.options = createAndSortOptions(newValue);
    if (props.modelValue) {
      setInternalValue(props.modelValue);
    }
  },
  { immediate: true }
);

useAttrs();
</script>

<style lang="scss" scoped>
.noResults {
  padding: 10px;
  text-align: center;
}
</style>
